import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";

export type PersonalBrandManager = Tables<"team_personal_brand_managers">;
export type PersonalBrandManagerInsert = TablesInsert<"team_personal_brand_managers">;

// Extended type with user data (first_name, last_name, email, avatar_url, phone from users table)
export interface PBMWithUserData extends PersonalBrandManager {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  phone: string | null;
}

export const getPersonalBrandManagers = async () => {
  try {
    const { data, error } = await supabase
      .from("team_personal_brand_managers")
      .select("*, users!personal_brand_managers_user_id_fkey(first_name, last_name, email, avatar_url, phone)")
      .order("user_id", { ascending: true });

    if (error) throw error;
    
    // Map user data to PBM level
    const mappedData = (data || []).map(pbm => ({
      ...pbm,
      first_name: (pbm as any).users?.first_name || null,
      last_name: (pbm as any).users?.last_name || null,
      email: (pbm as any).users?.email || null,
      avatar_url: (pbm as any).users?.avatar_url || null,
      phone: (pbm as any).users?.phone || null,
    }));
    
    // Sort by last_name after mapping
    mappedData.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    
    return { success: true, data: mappedData };
  } catch (error) {
    console.error("Error fetching personal brand managers:", error);
    return { success: false, error };
  }
};

export const getPersonalBrandManagerById = async (id: string) => {
  try {
    const { data, error } = await supabase
      .from("team_personal_brand_managers")
      .select("*, users!personal_brand_managers_user_id_fkey(first_name, last_name, email, avatar_url, phone, cal_username)")
      .eq("id", id)
      .single();

    if (error) throw error;
    
    // Map user data to PBM level
    const result = data ? {
      ...data,
      first_name: (data as any).users?.first_name || null,
      last_name: (data as any).users?.last_name || null,
      email: (data as any).users?.email || null,
      avatar_url: (data as any).users?.avatar_url || null,
      phone: (data as any).users?.phone || null,
      cal_username: (data as any).users?.cal_username || null,
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    console.error("Error fetching personal brand manager:", error);
    return { success: false, error };
  }
};

export const findPersonalBrandManagerByEmail = async (email: string) => {
  try {
    // Search via users table since email is now there
    const { data, error } = await supabase
      .from("team_personal_brand_managers")
      .select("id, user_id, users!personal_brand_managers_user_id_fkey!inner(first_name, last_name, email, avatar_url, phone)")
      .eq("users.email", email)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (error) throw error;
    
    // Map user data to PBM level
    const result = data ? {
      id: data.id,
      user_id: data.user_id,
      first_name: (data as any).users?.first_name || null,
      last_name: (data as any).users?.last_name || null,
      email: (data as any).users?.email || null,
      avatar_url: (data as any).users?.avatar_url || null,
      phone: (data as any).users?.phone || null,
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    console.error("Error finding personal brand manager by email:", error);
    return { success: false, error };
  }
};

/**
 * Get PBM staff record by user_id (for editing in admin)
 */
export const getPersonalBrandManagerByUserId = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("team_personal_brand_managers")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching PBM by user_id:", error);
    return { success: false, error };
  }
};

/**
 * Update PBM staff metadata (cal_slug, specialties, studio_id, bio, max_clients, is_active)
 */
export interface UpdatePBMData {
  cal_slug?: string | null;
  specialties?: string[] | null;
  studio_id?: string | null;
  bio?: string | null;
  max_clients?: number | null;
  is_active?: boolean | null;
}

export const updatePersonalBrandManager = async (userId: string, updates: UpdatePBMData) => {
  try {
    const { data, error } = await supabase
      .from("team_personal_brand_managers")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error("Error updating PBM:", error);
    return { success: false, error };
  }
};